/**
 * Cloud Search (xCloud Search & Filter) - tick several filter values, then apply them at once.
 *
 * Out of the box the app applies a value the moment it is clicked: the page navigates to
 * /a/search/... , the dropdown closes and you have to reopen it for every extra value. This script
 * intercepts those clicks, keeps the choices in memory, and only navigates when the Apply button
 * at the foot of the dropdown is pressed - one page load for any number of values.
 *
 * It uses the app's own URL scheme, so nothing else changes:
 *   one value    /a/search/cardigans?filter_color_filter=Black
 *   two values   /a/search/cardigans?filter_color_filter[0]=Black&filter_color_filter[1]=Blue
 *
 * If a click can't be matched to a filter the app keeps its original behaviour.
 */
(function () {
  'use strict';

  var VALUE_SELECTOR = '.cloud-search-filter-value';
  var PANEL_PREFIX = 'cloud-search-filter-values-';
  var PANEL_SELECTOR = '[id^="' + PANEL_PREFIX + '"]';
  var MOBILE_PANE_SELECTOR = '.cloud-search-filters-pane';
  var APPLY_LABEL = 'Apply';

  /* ---------------------------------------------------------------------------
   * Filter dropdown text - adjust the font here.
   *
   * These values mirror the nav dropdown, whose size lives in
   * sections/header.liquid as --ws-nav-font-size on page-header (12px from
   * 1400px wide, then 10 / 9 / 8px as the window narrows). That variable only
   * reaches the header, so the steps are repeated here. Change a size below and
   * the whole filter bar follows; phones are left at the app's own size.
   * ------------------------------------------------------------------------ */
  var FONT = {
    family: 'Raleway, sans-serif',
    weight: '400', // the values (Black, Blue, ...)
    headerWeight: '500', // the group titles (SIZE, COLOR, ...): medium, like the collection name
    // the group titles' letter spacing: the collection name's 1px at 16px, scaled to the filter size
    // (0.75px at 12px). Same font file and weight as the name; the spacing is what makes them look alike.
    headerLetterSpacing: '0.0625em',
    lineHeight: '1.2',
    color: '#171717', // the values (Black, Blue, ...)
    headerColor: '#676869', // the group titles and their arrows: the grey of the collection name (.collection-title-test h1)
    sizes: [
      // [min width, max width (0 = none), size]
      [1400, 0, '12px'],
      [1100, 1399.98, '10px'],
      [900, 1099.98, '9px'],
      [737, 899.98, '8px'] // from 737px, where xCloud shows the filter row instead of its "Filter" button (was 768)
    ]
  };

  // { filter_color_filter: ['Black', 'Blue'] } - starts as whatever the current URL has applied
  var pending = readFiltersFromUrl();

  function readFiltersFromUrl() {
    var filters = {};
    new URLSearchParams(window.location.search).forEach(function (value, key) {
      var match = key.match(/^(filter_[^[\]]+)(?:\[\d+\])?$/);
      if (!match) return;
      if (!filters[match[1]]) filters[match[1]] = [];
      if (filters[match[1]].indexOf(value) === -1) filters[match[1]].push(value);
    });
    return filters;
  }

  function paramForLabel(label) {
    var panel = label.closest(PANEL_SELECTOR);
    if (panel) return 'filter_' + panel.id.slice(PANEL_PREFIX.length);

    // mobile layout: the group carries the name, the matching header carries the id
    var group = label.closest('[data-filter-name]');
    if (!group) return null;
    var name = group.getAttribute('data-filter-name');
    var headers = document.querySelectorAll('.cloud-search-filter[aria-controls]');
    for (var i = 0; i < headers.length; i += 1) {
      if (headers[i].getAttribute('data-filter-name') === name) {
        return 'filter_' + headers[i].getAttribute('aria-controls').slice(PANEL_PREFIX.length);
      }
    }
    return null;
  }

  function toggleValue(param, value) {
    if (!pending[param]) pending[param] = [];
    var index = pending[param].indexOf(value);
    if (index === -1) {
      pending[param].push(value);
    } else {
      pending[param].splice(index, 1);
    }
  }

  function pendingCount() {
    return Object.keys(pending).reduce(function (total, param) {
      return total + pending[param].length;
    }, 0);
  }

  function buildUrl() {
    var path = window.location.pathname;
    if (path.indexOf('/a/search') !== 0) {
      var collection = path.match(/^\/collections\/([^/]+)/);
      path = collection ? '/a/search/' + collection[1] : '/a/search';
    }

    var params = new URLSearchParams();
    // keep anything that isn't a filter (search terms, sorting, ...)
    new URLSearchParams(window.location.search).forEach(function (value, key) {
      if (!/^filter_/.test(key)) params.append(key, value);
    });
    Object.keys(pending).forEach(function (param) {
      var values = pending[param];
      if (!values.length) return;
      if (values.length === 1) {
        params.append(param, values[0]);
      } else {
        values.forEach(function (value, index) {
          params.append(param + '[' + index + ']', value);
        });
      }
    });

    var query = params.toString();
    return path + (query ? '?' + query : '');
  }

  function syncPanel(panel) {
    var labels = panel.querySelectorAll(VALUE_SELECTOR);
    for (var i = 0; i < labels.length; i += 1) {
      var label = labels[i];
      var param = paramForLabel(label);
      var value = label.getAttribute('data-filter-value');
      var selected = !!(param && pending[param] && pending[param].indexOf(value) !== -1);
      var input = label.querySelector('input[type="checkbox"]');
      if (input && input.checked !== selected) input.checked = selected;
      if (label.classList.contains('cloud-search-filter-value--selected') !== selected) {
        label.classList.toggle('cloud-search-filter-value--selected', selected);
      }
    }
  }

  function updateBar(panel) {
    var button = panel.querySelector('.cs-apply__btn');
    if (!button) return;
    var count = pendingCount();
    var label = APPLY_LABEL;
    // only write when it changes: setting textContent is itself a DOM mutation, and the
    // observer below would call straight back into here
    if (button.textContent !== label) button.textContent = label;
  }

  function addBar(panel, sticky) {
    if (panel.querySelector('.cs-apply')) return;
    var bar = document.createElement('div');
    bar.className = sticky ? 'cs-apply cs-apply--sticky' : 'cs-apply';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cs-apply__btn';
    bar.appendChild(button);
    panel.appendChild(bar);
    updateBar(panel);
  }

  function refreshPanels() {
    // desktop: one bar per open dropdown
    var panels = document.querySelectorAll(PANEL_SELECTOR);
    for (var i = 0; i < panels.length; i += 1) {
      if (!panels[i].querySelector(VALUE_SELECTOR)) continue;
      syncPanel(panels[i]);
      addBar(panels[i], false);
      updateBar(panels[i]);
    }

    // mobile: every group lives in one scrolling pane, so one bar pinned to its foot
    var pane = document.querySelector(MOBILE_PANE_SELECTOR);
    if (pane && pane.querySelector(VALUE_SELECTOR)) {
      syncPanel(pane);
      addBar(pane, true);
      updateBar(pane);
    }
  }

  function apply(button) {
    if (button) {
      button.disabled = true;
      button.textContent = APPLY_LABEL + '...';
    }
    window.location.href = buildUrl();
  }

  // Capture on window so the click never reaches the app's own handler.
  window.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;

    var applyButton = target.closest('.cs-apply__btn');
    if (applyButton) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      apply(applyButton);
      return;
    }

    var label = target.closest(VALUE_SELECTOR);
    if (!label) return;
    var param = paramForLabel(label);
    if (!param) return; // unknown filter - let the app handle it as before

    evt.preventDefault();
    evt.stopImmediatePropagation();
    toggleValue(param, label.getAttribute('data-filter-value'));
    refreshPanels();
  }, true);

  function fontCss() {
    var header = '.cloud-search-filter__name';
    var values = '.cloud-search-filter-value,.cloud-search-filter-value__name,.cloud-search-filter-value__count';
    var text = header + ',' + values;
    var css = '@media (min-width:737px){' + text + '{'
      + 'font-family:' + FONT.family + '!important;'
      + 'line-height:' + FONT.lineHeight + '!important;}'
      + values + '{font-weight:' + FONT.weight + '!important;}'
      + header + '{font-weight:' + FONT.headerWeight + '!important;'
      + 'letter-spacing:' + FONT.headerLetterSpacing + '!important;}'
      + header + '{color:' + FONT.headerColor + '!important;}'
      + '.cloud-search-filter-value__name{color:' + FONT.color + '!important;}}';
    FONT.sizes.forEach(function (step) {
      css += '@media (min-width:' + step[0] + 'px)'
        + (step[1] ? ' and (max-width:' + step[1] + 'px)' : '')
        + '{' + text + '{font-size:' + step[2] + '!important;}}';
    });
    return css;
  }

  function start() {
    var style = document.createElement('style');
    style.textContent = [
      '.cs-apply{display:flex;gap:10px;align-items:center;',
      'padding:10px 12px;margin-top:2px;border-top:1px solid rgba(0,0,0,.12);background:#fff;}',
      '.cs-apply__btn{flex:1 1 auto;padding:9px 14px;border:0;background:#000;color:#fff;',
      'font:inherit;font-size:12px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;}',
      '.cs-apply--sticky{position:sticky;bottom:0;z-index:5;box-shadow:0 -8px 20px rgba(0,0,0,.08);}',
      '.cs-apply__btn[disabled]{opacity:.6;cursor:default;}',
      fontCss()
    ].join('');
    (document.head || document.documentElement).appendChild(style);

    new MutationObserver(refreshPanels).observe(document.body, { childList: true, subtree: true });
    refreshPanels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
